/**
 * 手势处理Hook
 */

import { useRef, useCallback } from 'react';

export interface GestureHandlers {
  onSwipeLeft?: () => void;
  onSwipeRight?: () => void;
  onSwipeUp?: () => void;
  onSwipeDown?: () => void;
  onTap?: () => void;
  onLongPress?: () => void;
}

export interface TouchState {
  startX: number;
  startY: number;
  startTime: number;
  isMoving: boolean;
}

/**
 * 手势识别Hook
 */
export function useGesture(handlers: GestureHandlers) {
  const touchState = useRef<TouchState | null>(null);
  const longPressTimer = useRef<NodeJS.Timeout | null>(null);

  const onTouchStart = useCallback((e: React.TouchEvent) => {
    const touch = e.touches[0];
    touchState.current = {
      startX: touch.clientX,
      startY: touch.clientY,
      startTime: Date.now(),
      isMoving: false,
    };

    // 开始长按计时器
    if (handlers.onLongPress) {
      longPressTimer.current = setTimeout(() => {
        if (touchState.current && !touchState.current.isMoving) {
          handlers.onLongPress?.();
        }
      }, 500);
    }
  }, [handlers]);

  const onTouchMove = useCallback((e: React.TouchEvent) => {
    if (!touchState.current) return;

    const touch = e.touches[0];
    const deltaX = Math.abs(touch.clientX - touchState.current.startX);
    const deltaY = Math.abs(touch.clientY - touchState.current.startY);

    // 如果移动距离超过阈值，标记为移动状态
    if (deltaX > 10 || deltaY > 10) {
      touchState.current.isMoving = true;
      
      // 取消长按
      if (longPressTimer.current) {
        clearTimeout(longPressTimer.current);
        longPressTimer.current = null;
      }
    }
  }, []);

  const onTouchEnd = useCallback((e: React.TouchEvent) => {
    if (!touchState.current) return;

    const touch = e.changedTouches[0];
    const deltaX = touch.clientX - touchState.current.startX;
    const deltaY = touch.clientY - touchState.current.startY;
    const deltaTime = Date.now() - touchState.current.startTime;

    // 取消长按计时器
    if (longPressTimer.current) {
      clearTimeout(longPressTimer.current);
      longPressTimer.current = null;
    }

    // 检查是否为点击
    if (!touchState.current.isMoving && deltaTime < 300) {
      handlers.onTap?.();
    }
    // 检查是否为滑动
    else if (touchState.current.isMoving) {
      const absDeltaX = Math.abs(deltaX);
      const absDeltaY = Math.abs(deltaY);

      // 水平滑动
      if (absDeltaX > absDeltaY && absDeltaX > 50) {
        if (deltaX > 0) {
          handlers.onSwipeRight?.();
        } else {
          handlers.onSwipeLeft?.();
        }
      }
      // 垂直滑动
      else if (absDeltaY > absDeltaX && absDeltaY > 50) {
        if (deltaY > 0) {
          handlers.onSwipeDown?.();
        } else {
          handlers.onSwipeUp?.();
        }
      }
    }

    touchState.current = null;
  }, [handlers]);

  return {
    onTouchStart,
    onTouchMove,
    onTouchEnd,
  };
}